import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { AlgoliaArea } from '../../../types/areas';
import { AlgoliaTrail } from '../../../types/trails';
import { AlgoliaAlert } from '../../../types/alerts';
import { Group } from '../../../types/groups';
import { AlgoliaObjectType, getTypeFilters, getGroupFilters } from '../../../utils/algoliaHelper';
import { search } from '../../../utils/requests/algoliaRequests';

type AlgoliaObjects = AlgoliaTrail | AlgoliaArea | AlgoliaAlert;
export type SearchState = {
  results: Array<AlgoliaObjects>;
  loading: boolean;
  error: SerializedError;
};

export const initialState: SearchState = {
  results: [],
  loading: false,
  error: null
};

type SearchParams = {
  indexId: string;
  query?: string;
  groups: Group[];
};

export const fetchResults = createAsyncThunk('search/fetchResults', async ({ indexId, query, groups }: SearchParams) => {
  // Prevent searching without any group_ids filter
  if (groups.length === 0) {
    return [];
  }

  const groupFilters = getGroupFilters(groups);
  const typeFilters = getTypeFilters([AlgoliaObjectType.Area, AlgoliaObjectType.Trail]);
  const filters = `(${typeFilters}) AND (${groupFilters})`;
  const attributesToRetrieve = [
    'ID',
    'name',
    'slug',
    'length',
    'difficulty_rating',
    'avg_rating',
    'num_reviews',
    'area_name',
    'area_slug',
    'city_name',
    'state_name',
    'country_name',
    'alert_ids'
  ];

  const response = await search<AlgoliaObjects>(indexId, query || '', { attributesToRetrieve, filters, hitsPerPage: 50 });
  return response.hits;
});

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchResults.pending, state => {
        state.loading = true;
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload;
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});

export default searchSlice.reducer;
